import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from '../context/ThemeContext';
import toast from 'react-hot-toast';
import '../styles/responsive/pages/PaymentLinks.css';
import {
  Plus,
  Copy,
  Check,
  ExternalLink,
  MoreVertical,
  ArrowLeft,
  X,
  Trash2,
  Power,
  Eye,
  EyeOff,
  Key,
  Link,
  Activity,
  BookOpen,
  Code,
  Terminal,
  Shield,
  Zap,
} from 'lucide-react';
import DataTable from '../components/common/DataTable';
import { TableSkeleton } from '../components/common/Skeleton';
import { formatUSD } from '../utils/formatAmount';
import { fetchWithAuth } from '../utils/api';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const PaymentLinks = () => {
  const { token, user } = useSelector((state) => state.auth);
  const { mode } = useTheme();

  const isAdmin = user?.role_id === 1 || user?.roleId === 1;

  const [currentView, setCurrentView] = useState('apiKeys');

  const [merchants, setMerchants] = useState([]);
  const [selectedMerchant, setSelectedMerchant] = useState('all');

  const [showCreateApiKeyModal, setShowCreateApiKeyModal] = useState(false);
  const [showCreateLinkModal, setShowCreateLinkModal] = useState(false);
  const [showUsageGuideDrawer, setShowUsageGuideDrawer] = useState(false);
  const [showSessionDrawer, setShowSessionDrawer] = useState(false);

  const [copiedId, setCopiedId] = useState(null);
  const [copiedApiKey, setCopiedApiKey] = useState(null);

  const [selectedApiKey, setSelectedApiKey] = useState(null);
  const [selectedLink, setSelectedLink] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionTransactions, setSessionTransactions] = useState([]);

  const [sessionsDateRange, setSessionsDateRange] = useState([null, null]);
  const [apiKeysDateRange, setApiKeysDateRange] = useState([null, null]);

  const [activeDropdown, setActiveDropdown] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ show: false, type: '', id: null, name: '' });
  const [isDeleting, setIsDeleting] = useState(false);
  const [showApiKey, setShowApiKey] = useState({});

  const [apiKeys, setApiKeys] = useState([]);
  const [paymentLinks, setPaymentLinks] = useState([]);
  const [paymentSessions, setPaymentSessions] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [apiKeyStats, setApiKeyStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    revoked: 0,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingLinks, setIsLoadingLinks] = useState(false);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const [isLoadingSessionDetail, setIsLoadingSessionDetail] = useState(false);

  const [apiKeyPagination, setApiKeyPagination] = useState({
    page: 1, limit: 10, total: 0, totalPages: 1,
  });
  const [linkPagination, setLinkPagination] = useState({
    page: 1, limit: 10, total: 0, totalPages: 1,
  });
  const [sessionsPagination, setSessionsPagination] = useState({
    page: 1, limit: 10, total: 0, totalPages: 1,
  });
  const [txPagination, setTxPagination] = useState({
    page: 1, limit: 10, total: 0, totalPages: 1,
  });

  const [apiKeyForm, setApiKeyForm] = useState({
    name: '',
    description: '',
    webhookUrl: '',
  });
  const [linkForm, setLinkForm] = useState({
    name: '',
    description: '',
    webhookUrl: '',
  });
  const [isCreating, setIsCreating] = useState(false);

  const fetchMerchants = useCallback(async () => {
    if (!token || !isAdmin) return;

    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/merchants?limit=100`);
      const data = await response.json();

      if (data.success && data.data?.merchants) {
        setMerchants(data.data.merchants);
      }
    } catch (error) {
      console.error('Failed to fetch merchants:', error);
    }
  }, [token, isAdmin]);

  const fetchApiKeys = useCallback(async (page = 1, limit = 10, merchantId = 'all', startDate = null, endDate = null) => {
    if (!token) return;

    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      params.append('isTestnet', mode === 'test' ? 'true' : 'false');

      if (isAdmin && merchantId && merchantId !== 'all') {
        params.append('merchantId', merchantId);
      }

      if (startDate) {
        params.append('startDate', startDate.toISOString());
      }
      if (endDate) {
        params.append('endDate', endDate.toISOString());
      }

      const endpoint = isAdmin ? `${API_BASE_URL}/api-keys/admin/all` : `${API_BASE_URL}/api-keys`;

      const response = await fetchWithAuth(`${endpoint}?${params}`);
      const data = await response.json();

      if (data.success) {
        setApiKeys(data.data.apiKeys);
        setApiKeyPagination({
          page: data.data.pagination.page,
          limit: data.data.pagination.limit,
          total: data.data.pagination.total,
          totalPages: data.data.pagination.totalPages,
        });
      }
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
    } finally {
      setIsLoading(false);
    }
  }, [token, isAdmin, mode]);

  const fetchApiKeyStats = useCallback(async () => {
    if (!token) return;

    try {
      const endpoint = isAdmin ? `${API_BASE_URL}/api-keys/admin/stats` : `${API_BASE_URL}/api-keys/stats`;

      const params = new URLSearchParams({
        isTestnet: mode === 'test' ? 'true' : 'false',
      });

      const response = await fetchWithAuth(`${endpoint}?${params}`);
      const data = await response.json();

      if (data.success) {
        setApiKeyStats(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch API key stats:', error);
    }
  }, [token, isAdmin, mode]);

  const fetchPaymentLinks = useCallback(async (apiKeyId, page = 1, limit = 10) => {
    if (!token || !apiKeyId) return;

    setIsLoadingLinks(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      const response = await fetchWithAuth(`${API_BASE_URL}/api-keys/${apiKeyId}/payment-links?${params}`);
      const data = await response.json();

      if (data.success) {
        setPaymentLinks(data.data.paymentLinks);
        setSelectedApiKey(prev => ({
          ...prev,
          ...data.data.apiKey,
        }));
        setLinkPagination({
          page: data.data.pagination.page,
          limit: data.data.pagination.limit,
          total: data.data.pagination.total,
          totalPages: data.data.pagination.totalPages,
        });
      }
    } catch (error) {
      console.error('Failed to fetch payment links:', error);
    } finally {
      setIsLoadingLinks(false);
    }
  }, [token]);

  const fetchSessions = useCallback(async (apiKeyId, page = 1, limit = 10, startDate = null, endDate = null) => {
    if (!token || !apiKeyId) return;

    setIsLoadingSessions(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      params.append('isTestnet', mode === 'test' ? 'true' : 'false');

      if (startDate) {
        params.append('startDate', startDate.toISOString());
      }
      if (endDate) {
        params.append('endDate', endDate.toISOString());
      }

      const response = await fetchWithAuth(`${API_BASE_URL}/api-keys/${apiKeyId}/sessions?${params}`);
      const data = await response.json();

      if (data.success) {
        setPaymentSessions(data.data.sessions);
        setSelectedApiKey(prev => ({
          ...prev,
          ...data.data.apiKey,
        }));
        setSessionsPagination({
          page: data.data.pagination.page,
          limit: data.data.pagination.limit,
          total: data.data.pagination.total,
          totalPages: data.data.pagination.totalPages,
        });
      }
    } catch (error) {
      console.error('Failed to fetch payment sessions:', error);
    } finally {
      setIsLoadingSessions(false);
    }
  }, [token, mode]);

  const fetchTransactions = useCallback(async (linkId, page = 1, limit = 10) => {
    if (!token || !linkId) return;

    setIsLoadingTransactions(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      const response = await fetchWithAuth(`${API_BASE_URL}/payment-links/${linkId}/transactions?${params}`);
      const data = await response.json();

      if (data.success) {
        setTransactions(data.data.transactions);
        setSelectedLink(prev => ({
          ...prev,
          ...data.data.paymentLink,
        }));
        setTxPagination({
          page: data.data.pagination.page,
          limit: data.data.pagination.limit,
          total: data.data.pagination.total,
          totalPages: data.data.pagination.totalPages,
        });
      }
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setIsLoadingTransactions(false);
    }
  }, [token]);

  const fetchSessionDetails = useCallback(async (sessionId) => {
    if (!token || !sessionId) return;

    setIsLoadingSessionDetail(true);
    try {
      const sessionResponse = await fetchWithAuth(`${API_BASE_URL}/v1/payments/sessions/${sessionId}?viewOnly=true`);
      const sessionData = await sessionResponse.json();
      console.log('[fetchSessionDetails] Session data:', sessionData);

      if (sessionData.success) {
        setSelectedSession(sessionData.data);

        try {
          const txResponse = await fetchWithAuth(`${API_BASE_URL}/v1/payments/sessions/${sessionId}/transactions`);
          const txData = await txResponse.json();
          console.log('[fetchSessionDetails] Transactions response:', txData);

          if (txData.success && txData.data) {
            const txList = txData.data.transactions || txData.data || [];
            console.log('[fetchSessionDetails] Setting transactions:', txList);
            setSessionTransactions(txList);
          } else {
            console.log('[fetchSessionDetails] No transactions found');
            setSessionTransactions([]);
          }
        } catch (txError) {
          console.error('[fetchSessionDetails] Error fetching transactions:', txError);
          setSessionTransactions([]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch session details:', error);
      toast.error('Failed to load session details');
    } finally {
      setIsLoadingSessionDetail(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchApiKeys(1, 10, selectedMerchant);
      fetchApiKeyStats();
      if (isAdmin) {
        fetchMerchants();
      }
    }
  }, [token, fetchApiKeys, fetchApiKeyStats, fetchMerchants, isAdmin, selectedMerchant, mode]);

  useEffect(() => {
    if (currentView === 'sessions' && selectedApiKey) {
      fetchSessions(selectedApiKey.id, 1, sessionsPagination.limit);
    }
  }, [mode]);

  const handleCopyLink = (id, url, e) => {
    if (e) e.stopPropagation();
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    toast.success('Link copied to clipboard');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleCopyApiKey = (id, apiKey, e) => {
    if (e) e.stopPropagation();
    navigator.clipboard.writeText(apiKey);
    setCopiedApiKey(id);
    toast.success('API Key copied to clipboard');
    setTimeout(() => setCopiedApiKey(null), 2000);
  };

  const toggleShowApiKey = (id, e) => {
    if (e) e.stopPropagation();
    setShowApiKey(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleCloseApiKeyModal = () => {
    setShowCreateApiKeyModal(false);
    setApiKeyForm({ name: '', description: '', webhookUrl: '' });
  };

  const handleCloseLinkModal = () => {
    setShowCreateLinkModal(false);
    setLinkForm({ name: '', description: '', webhookUrl: '' });
  };

  const handleCreateApiKey = async () => {
    if (!apiKeyForm.name) return;

    setIsCreating(true);
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api-keys`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          name: apiKeyForm.name,
          description: apiKeyForm.description,
          webhookUrl: apiKeyForm.webhookUrl || null,
        }),
      });

      const data = await response.json();

      if (data.success) {
        handleCloseApiKeyModal();
        fetchApiKeys(apiKeyPagination.page, apiKeyPagination.limit);
        fetchApiKeyStats();
        toast.success('API key created successfully');

        toast((t) => (
          <div>
            <p><strong>Your API Key:</strong></p>
            <p style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', wordBreak: 'break-all' }}>
              {data.data.apiKey}
            </p>
            {data.data.secretKey && (
              <>
                <p style={{ marginTop: 'var(--space-2)' }}><strong>Your API Secret:</strong></p>
                <p style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', wordBreak: 'break-all' }}>
                  {data.data.secretKey}
                </p>
              </>
            )}
            <p style={{ color: 'var(--warn)', fontSize: '12px', marginTop: 'var(--space-2)' }}>
              Save the secret on your server now. You can reveal or rotate it later under Developer → API Keys.
            </p>
            <button
              onClick={() => {
                navigator.clipboard.writeText(data.data.apiKey);
                toast.dismiss(t.id);
                toast.success('API Key copied!');
              }}
              style={{ marginTop: '8px', padding: '4px 8px', cursor: 'pointer' }}
            >
              Copy API Key
            </button>
            {data.data.secretKey && (
              <button
                onClick={() => {
                  navigator.clipboard.writeText(data.data.secretKey);
                  toast.success('API Secret copied!');
                }}
                style={{ marginTop: '8px', marginLeft: '8px', padding: '4px 8px', cursor: 'pointer' }}
              >
                Copy API Secret
              </button>
            )}
          </div>
        ), { duration: 60000 });
      } else {
        toast.error(data.message || 'Failed to create API key');
      }
    } catch (error) {
      console.error('Failed to create API key:', error);
      toast.error('Failed to create API key');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreatePaymentLink = async () => {
    if (!linkForm.name || !selectedApiKey) return;

    setIsCreating(true);
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/payment-links`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          name: linkForm.name,
          description: linkForm.description,
          webhookUrl: linkForm.webhookUrl || null,
          apiKeyId: selectedApiKey.id,
          amount: null,
          currency: 'USD',
          isOneTime: false, 
          isTestnet: mode === 'test', 
        }),
      });

      const data = await response.json();

      if (data.success) {
        handleCloseLinkModal();
        fetchPaymentLinks(selectedApiKey.id, linkPagination.page, linkPagination.limit);
        toast.success('Payment link created successfully');
      } else {
        toast.error(data.message || 'Failed to create payment link');
      }
    } catch (error) {
      console.error('Failed to create payment link:', error);
      toast.error('Failed to create payment link');
    } finally {
      setIsCreating(false);
    }
  };

  const handleToggleApiKeyStatus = async (id, e) => {
    e.stopPropagation();
    setActiveDropdown(null);

    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api-keys/${id}/toggle`, {
        method: 'PATCH',
      });

      const data = await response.json();

      if (data.success) {
        fetchApiKeys(apiKeyPagination.page, apiKeyPagination.limit);
        fetchApiKeyStats();
        toast.success('Status updated successfully');
      } else {
        toast.error(data.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Failed to toggle API key status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleToggleLinkStatus = async (linkId, e) => {
    e.stopPropagation();
    setActiveDropdown(null);

    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/payment-links/${linkId}/toggle-status`, {
        method: 'PATCH',
      });

      const data = await response.json();

      if (data.success) {
        fetchPaymentLinks(selectedApiKey.id, linkPagination.page, linkPagination.limit);
        toast.success('Status updated successfully');
      } else {
        toast.error(data.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Failed to toggle payment link status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleDeleteApiKey = (id, name, e) => {
    e.stopPropagation();
    setActiveDropdown(null);
    setDeleteModal({ show: true, type: 'apiKey', id, name });
  };

  const handleDeletePaymentLink = (linkId, linkName, e) => {
    e.stopPropagation();
    setActiveDropdown(null);
    setDeleteModal({ show: true, type: 'paymentLink', id: linkId, name: linkName });
  };

  const confirmDelete = async () => {
    if (!deleteModal.id) return;

    setIsDeleting(true);
    try {
      const endpoint = deleteModal.type === 'apiKey'
        ? `${API_BASE_URL}/api-keys/${deleteModal.id}`
        : `${API_BASE_URL}/payment-links/${deleteModal.id}`;

      const response = await fetchWithAuth(endpoint, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (data.success) {
        setDeleteModal({ show: false, type: '', id: null, name: '' });

        if (deleteModal.type === 'apiKey') {
          fetchApiKeys(apiKeyPagination.page, apiKeyPagination.limit);
          fetchApiKeyStats();
          toast.success('API key revoked successfully');
        } else {
          fetchPaymentLinks(selectedApiKey.id, linkPagination.page, linkPagination.limit);
          toast.success('Payment link deleted successfully');
        }
      } else {
        toast.error(data.message || 'Failed to delete');
      }
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error('Failed to delete');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleApiKeyClick = (apiKey) => {
    setSelectedApiKey(apiKey);
    setCurrentView('sessions');
    fetchSessions(apiKey.id, 1, 10);
    fetchPaymentLinks(apiKey.id, 1, 10);
  };

  const handleLinkClick = (link) => {
    setSelectedLink(link);
    setCurrentView('paymentLinkDetail');
  };

  const handleViewTransactions = () => {
    if (selectedLink) {
      setCurrentView('transactions');
      fetchTransactions(selectedLink.linkId, 1, 10);
    }
  };

  const handleTransactionClick = (transaction) => {
    setSelectedTransaction(transaction);
    setCurrentView('transactionDetail');
  };

  const handleSessionClick = (session) => {
    setSelectedSession(session);
    setShowSessionDrawer(true);
    fetchSessionDetails(session.sessionId);
  };

  const handleCloseSessionDrawer = () => {
    setShowSessionDrawer(false);
    setSelectedSession(null);
    setSessionTransactions([]);
  };

  const handleSessionsDateRangeChange = (startDate, endDate) => {
    setSessionsDateRange([startDate, endDate]);
    if ((startDate && endDate) || (!startDate && !endDate)) {
      if (selectedApiKey) {
        fetchSessions(selectedApiKey.id, 1, sessionsPagination.limit, startDate, endDate);
      }
    }
  };

  const handleApiKeysDateRangeChange = (startDate, endDate) => {
    setApiKeysDateRange([startDate, endDate]);
    if ((startDate && endDate) || (!startDate && !endDate)) {
      fetchApiKeys(1, apiKeyPagination.limit, selectedMerchant, startDate, endDate);
    }
  };

  const handleBack = () => {
    if (currentView === 'transactionDetail') {
      setCurrentView('transactions');
      setSelectedTransaction(null);
    } else if (currentView === 'transactions') {
      setCurrentView('paymentLinkDetail');
      setTransactions([]);
    } else if (currentView === 'paymentLinkDetail') {
      setCurrentView('sessions');
      setSelectedLink(null);
    } else if (currentView === 'paymentLinks') {
      setCurrentView('sessions');
    } else if (currentView === 'sessions') {
      setCurrentView('apiKeys');
      setSelectedApiKey(null);
      setPaymentSessions([]);
      setPaymentLinks([]);
    }
  };

  const handleMerchantChange = (e) => {
    setSelectedMerchant(e.target.value);
    fetchApiKeys(1, apiKeyPagination.limit, e.target.value);
  };

  const handleApiKeyPageChange = (newPage) => {
    fetchApiKeys(newPage, apiKeyPagination.limit, selectedMerchant);
  };

  const handleLinkPageChange = (newPage) => {
    if (selectedApiKey) {
      fetchPaymentLinks(selectedApiKey.id, newPage, linkPagination.limit);
    }
  };

  const handleSessionsPageChange = (newPage) => {
    if (selectedApiKey) {
      fetchSessions(selectedApiKey.id, newPage, sessionsPagination.limit);
    }
  };

  const handleTxPageChange = (newPage) => {
    if (selectedLink) {
      fetchTransactions(selectedLink.linkId, newPage, txPagination.limit);
    }
  };

  useEffect(() => {
    const handleClickOutside = () => setActiveDropdown(null);
    if (activeDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [activeDropdown]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const maskApiKey = (apiKey) => {
    if (!apiKey) return '';
    return apiKey.substring(0, 12) + '...' + apiKey.substring(apiKey.length - 4);
  };

  const apiKeyColumns = [
    {
      header: 'Name',
      accessor: 'name',
      sortable: true,
      render: (value, row) => (
        <div className="link-name-cell">
          <span className="link-name">{value}</span>
          <span className="link-description">{row.description || 'No description'}</span>
        </div>
      ),
    },
    {
      header: 'API Key',
      accessor: 'apiKey',
      sortable: false,
      render: (value, row) => (
        <div className="api-key-cell" onClick={(e) => e.stopPropagation()}>
          <code className="api-key-value">
            {showApiKey[row.id] ? value : maskApiKey(value)}
       
          </code>
          <button
            className="action-icon-btn small"
            onClick={(e) => toggleShowApiKey(row.id, e)}
            title={showApiKey[row.id] ? 'Hide API Key' : 'Show API Key'}
          >
            {showApiKey[row.id] ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
          <button
            className="action-icon-btn small"
            onClick={(e) => handleCopyApiKey(row.id, value, e)}
            title="Copy API Key"
          >
            {copiedApiKey === row.id ? <Check size={14} /> : <Copy size={14} />}
          </button>
        </div>
      ),
    },
    {
      header: 'Status',
      accessor: 'status',
      type: 'status',
      sortable: true,
    },
    {
      header: 'Last Used',
      accessor: 'lastUsedAt',
      sortable: true,
      render: (value) => formatDate(value),
    },
    {
      header: 'Created',
      accessor: 'createdAt',
      type: 'date',
      sortable: true,
    },
    {
      header: 'Actions',
      accessor: 'actions',
      sortable: false,
      render: (_, row) => (
        <div className="action-buttons" onClick={(e) => e.stopPropagation()}>
          <div className="action-dropdown">
            <button
              className="action-icon-btn"
              onClick={(e) => {
                e.stopPropagation();
                setActiveDropdown(activeDropdown === row.id ? null : row.id);
              }}
            >
              <MoreVertical size={16} />
            </button>
            {activeDropdown === row.id && (
              <div className="action-dropdown-menu">
                {row.status === 'active' && (
                  <button
                    className="dropdown-item"
                    onClick={(e) => handleToggleApiKeyStatus(row.id, e)}
                  >
                    <Power size={14} />
                    Deactivate Key
                  </button>
                )}
                {row.status === 'inactive' && (
                  <button
                    className="dropdown-item"
                    onClick={(e) => handleToggleApiKeyStatus(row.id, e)}
                  >
                    <Power size={14} />
                    Activate Key
                  </button>
                )}
                <button
                  className="dropdown-item danger"
                  onClick={(e) => handleDeleteApiKey(row.id, row.name, e)}
                >
                  <Trash2 size={14} />
                  Revoke Key
                </button>
              </div>
            )}
          </div>
        </div>
      ),
    },
  ];

  const linkColumns = [
    {
      header: 'Name',
      accessor: 'name',
      sortable: true,
      render: (value, row) => (
        <div className="link-name-cell">
          <span className="link-name">{value}</span>
          <span className="link-description">{row.description || 'No description'}</span>
        </div>
      ),
    },
    {
      header: 'Status',
      accessor: 'status',
      type: 'status',
      sortable: true,
    },
    {
      header: 'Payments',
      accessor: 'paymentsCount',
      sortable: true,
      render: (value) => value || 0,
    },
    {
      header: 'Total Collected',
      accessor: 'totalCollected',
      sortable: true,
      render: (value) => `$${formatUSD(value)}`,
    },
    {
      header: 'Link',
      accessor: 'url',
      sortable: false,
      render: (value, row) => (
        <div className="link-url-cell" onClick={(e) => e.stopPropagation()}>
          <button
            className="action-icon-btn"
            onClick={(e) => handleCopyLink(row.linkId, value, e)}
            title="Copy link"
          >
            {copiedId === row.linkId ? <Check size={16} /> : <Copy size={16} />}
          </button>
          <button
            className="action-icon-btn"
            onClick={(e) => {
              e.stopPropagation();
              window.open(value, '_blank');
            }}
            title="Open link"
          >
            <ExternalLink size={16} />
          </button>
        </div>
      ),
    },
    {
      header: 'Created',
      accessor: 'createdAt',
      type: 'date',
      sortable: true,
    },
    {
      header: 'Actions',
      accessor: 'actions',
      sortable: false,
      render: (_, row) => (
        <div className="action-buttons" onClick={(e) => e.stopPropagation()}>
          <div className="action-dropdown">
            <button
              className="action-icon-btn"
              onClick={(e) => {
                e.stopPropagation();
                setActiveDropdown(activeDropdown === row.linkId ? null : row.linkId);
              }}
            >
              <MoreVertical size={16} />
            </button>
            {activeDropdown === row.linkId && (
              <div className="action-dropdown-menu">
                {row.status === 'active' && (
                  <button
                    className="dropdown-item"
                    onClick={(e) => handleToggleLinkStatus(row.linkId, e)}
                  >
                    <Power size={14} />
                    Deactivate Link
                  </button>
                )}
                {row.status === 'inactive' && (
                  <button
                    className="dropdown-item"
                    onClick={(e) => handleToggleLinkStatus(row.linkId, e)}
                  >
                    <Power size={14} />
                    Activate Link
                  </button>
                )}
                <button
                  className="dropdown-item danger"
                  onClick={(e) => handleDeletePaymentLink(row.linkId, row.name, e)}
                >
                  <Trash2 size={14} />
                  Delete Link
                </button>
              </div>
            )}
          </div>
        </div>
      ),
    },
  ];

  const sessionColumns = [
    {
      header: 'Order ID',
      accessor: 'orderId',
      sortable: true,
      render: (value, row) => (
        <div className="link-name-cell">
          <span className="link-name">{value}</span>
          <span className="link-description">{row.description || row.sessionId}</span>
        </div>
      ),
    },
    {
      header: 'Amount',
      accessor: 'amount',
      sortable: true,
      render: (value, row) => (
        <span className="amount">
          ${formatUSD(value)} {row.currency}
        </span>
      ),
    },
    {
      header: 'Paid',
      accessor: 'totalPaidUsd',
      sortable: true,
      render: (value, row) => (
        <span className={`amount ${value >= row.amount ? 'text-success' : value > 0 ? 'text-warning' : ''}`}>
          ${formatUSD(value || 0)}
        </span>
      ),
    },
    {
      header: 'Status',
      accessor: 'status',
      type: 'status',
      sortable: true,
    },
    {
      header: 'Created',
      accessor: 'createdAt',
      type: 'date',
      sortable: true,
    },
  ];

  const transactionColumns = [
    {
      header: 'Name',
      accessor: 'customerName',
      sortable: true,
      render: (value, row) => (
        <div className="link-name-cell">
          <span className="link-name">{value || 'Anonymous'}</span>
          <span className="link-id">{row.transactionId}</span>
        </div>
      ),
    },
    {
      header: 'Amount',
      accessor: 'amount',
      sortable: true,
      render: (value, row) => (
        <span className="amount">
          ${formatUSD(value)} {row.currency}
        </span>
      ),
    },
    {
      header: 'Status',
      accessor: 'status',
      type: 'status',
      sortable: true,
    },
    {
      header: 'Created',
      accessor: 'createdAt',
      type: 'date',
      sortable: true,
    },
    {
      header: 'From Address',
      accessor: 'fromAddress',
      sortable: false,
      render: (value) => (
        <span className="address-cell" title={value}>
          {value ? `${value.substring(0, 8)}...${value.substring(value.length - 6)}` : '-'}
        </span>
      ),
    },
  ];

  if (currentView === 'transactionDetail' && selectedTransaction) {
    return (
      <div className="payment-link-detail-page">
        <button className="back-btn" onClick={handleBack}>
          <ArrowLeft size={18} />
          Back to transactions
        </button>

        <div className="detail-header">
          <div className="detail-header-left">
            <span className="detail-label">TRANSACTION</span>
            <h1 className="detail-title">
              {selectedTransaction.transactionId}
            </h1>
            <p className="detail-subtitle">
              Transaction details for payment via {selectedApiKey?.name || selectedLink?.name}
            </p>
          </div>
          <div className="detail-header-right">
            <span className={`status-badge-large ${selectedTransaction.status}`}>
              {selectedTransaction.status}
            </span>
          </div>
        </div>

        <div className="detail-content full-width">
          <div className="detail-main">
            <div className="detail-section">
              <h3 className="section-title">Transaction Details</h3>
              <div className="detail-grid">
                <div className="detail-row">
                  <span className="detail-key">Transaction ID</span>
                  <span className="detail-value">{selectedTransaction.transactionId}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-key">Amount</span>
                  <span className="detail-value">
                    ${formatUSD(selectedTransaction.amount)} {selectedTransaction.currency}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-key">Status</span>
                  <span className={`status-badge ${selectedTransaction.status}`}>
                    {selectedTransaction.status}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-key">Customer Name</span>
                  <span className="detail-value">{selectedTransaction.customerName || 'Anonymous'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-key">Customer Email</span>
                  <span className="detail-value">{selectedTransaction.customerEmail || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-key">Network</span>
                  <span className="detail-value">{selectedTransaction.network || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-key">From Address</span>
                  <span className="detail-value" style={{ fontFamily: 'var(--font-mono)', fontSize: '12px' }}>
                    {selectedTransaction.fromAddress || '-'}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-key">To Address</span>
                  <span className="detail-value" style={{ fontFamily: 'var(--font-mono)', fontSize: '12px' }}>
                    {selectedTransaction.toAddress || '-'}
                  </span>
                </div>
                {selectedTransaction.txHash && (
                  <div className="detail-row">
                    <span className="detail-key">Transaction Hash</span>
                    <span className="detail-value" style={{ fontFamily: 'var(--font-mono)', fontSize: '12px' }}>
                      {selectedTransaction.txHash}
                    </span>
                  </div>
                )}
                <div className="detail-row">
                  <span className="detail-key">Created At</span>
                  <span className="detail-value">{formatDate(selectedTransaction.createdAt)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (currentView === 'paymentLinkDetail' && selectedLink) {
    return (
      <div className="payment-link-detail-page">
        <button className="back-btn" onClick={handleBack}>
          <ArrowLeft size={18} />
          Back to payment links
        </button>


        <div className="detail-content">
          <div className="detail-left-column">
            <div className="detail-section card">
              <h3 className="section-title">Details</h3>
              <div className="detail-grid">
                <div className="detail-row">
                  <span className="detail-key">Name</span>
                  <span className="detail-value">{selectedLink.name}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-key">Description</span>
                  <span className="detail-value">{selectedLink.description || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-key">Amount</span>
                  <span className="detail-value">
                    {selectedLink.amount ? `$${formatUSD(selectedLink.amount)}` : 'Dynamic'}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-key">Status</span>
                  <span className={`status-badge ${selectedLink.status}`}>
                    {selectedLink.status}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-key">Date created</span>
                  <span className="detail-value">{formatDate(selectedLink.createdAt)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-key">Expires</span>
                  <span className="detail-value">{selectedLink.expiresAt ? formatDate(selectedLink.expiresAt) : 'Never'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-key">Network</span>
                  <span className="detail-value">{selectedLink.network || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-key">Token</span>
                  <span className="detail-value">{selectedLink.token || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-key">Usage</span>
                  <span className="detail-value">{selectedLink.paymentsCount || 0} times</span>
                </div>
                <div className="detail-row">
                  <span className="detail-key">Total Collected</span>
                  <span className="detail-value">${formatUSD(selectedLink.totalCollected)}</span>
                </div>
              </div>
            </div>

            <div className="detail-section card">
              <div className="empty-payments-section">
                <Link size={32} className="empty-icon" />
                <p className="empty-title">
                  {selectedLink.paymentsCount > 0
                    ? `${selectedLink.paymentsCount} payment${selectedLink.paymentsCount > 1 ? 's' : ''} received`
                    : 'No payments yet'}
                </p>
                <p className="empty-subtitle">
                  {selectedLink.paymentsCount > 0
                    ? 'Click below to view all transactions.'
                    : 'Share this link with customers to start receiving payments.'}
                </p>
                {selectedLink.paymentsCount > 0 && (
                  <button className="btn-primary" onClick={handleViewTransactions}>
                    <Activity size={16} />
                    View Transactions
                  </button>
                )}
              </div>
            </div>

            <div className="detail-section card">
              <h3 className="section-title">Events</h3>
              <div className="events-list">
                <div className="event-item">
                  <span className="event-name">Payment link created</span>
                  <span className="event-date">{formatDate(selectedLink.createdAt)}</span>
                </div>
              </div>
            </div>
          </div>

         
        </div>
      </div>
    );
  }

  if (currentView === 'transactions' && selectedLink) {
    return (
      <div className="payment-link-detail-page">
        <button className="back-btn" onClick={handleBack}>
          <ArrowLeft size={18} />
          Back to payment link
        </button>

        <div className="detail-header">
          <div className="detail-header-left">
            <span className="detail-label">PAYMENT LINK</span>
            <h1 className="detail-title">{selectedLink.name}</h1>
            <p className="detail-subtitle">
              {selectedLink.description || 'View all transactions for this payment link'}
            </p>
          </div>
          <div className="detail-header-right">
            <span className={`status-badge-large ${selectedLink.status}`}>
              {selectedLink.status}
            </span>
          </div>
        </div>

        <div className="info-cards-row">
          <div className="info-card">
            <div className="info-card-header">
              <ExternalLink size={16} />
              <span>Payment URL</span>
            </div>
            <div className="info-card-content">
              <code>{selectedLink.url}</code>
              <button
                className="btn-icon small"
                onClick={() => {
                  navigator.clipboard.writeText(selectedLink.url);
                  toast.success('URL copied!');
                }}
              >
                <Copy size={14} />
              </button>
            </div>
          </div>
          <div className="info-card">
            <div className="info-card-header">
              <Key size={16} />
              <span>Link ID</span>
            </div>
            <div className="info-card-content">
              <code>{selectedLink.linkId}</code>
              <button
                className="btn-icon small"
                onClick={() => {
                  navigator.clipboard.writeText(selectedLink.linkId);
                  toast.success('Link ID copied!');
                }}
              >
                <Copy size={14} />
              </button>
            </div>
          </div>
        </div>

        <div className="stats-row">
          <div className="stat-item">
            <span className="stat-label">Total Transactions</span>
            <span className="stat-value">{txPagination.total || 0}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Total Collected</span>
            <span className="stat-value">${formatUSD(selectedLink.totalCollected)}</span>
          </div>
        </div>

        <div className="card">
          <h3 className="section-title" style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-color)' }}>
            Transactions
          </h3>
          {isLoadingTransactions ? (
            <TableSkeleton rows={4} cols={5} />
          ) : (
            <DataTable
              columns={transactionColumns}
              data={transactions}
              searchable={true}
              sortable={true}
              paginated={true}
              pageSize={txPagination.limit}
              emptyMessage="No transactions yet for this payment link."
              onRowClick={handleTransactionClick}
              serverSidePagination={true}
              totalRecords={txPagination.total}
              currentServerPage={txPagination.page}
              onPageChange={handleTxPageChange}
            />
          )}
        </div>
      </div>
    );
  }

  if (currentView === 'sessions' && selectedApiKey) {
    return (
      <div className="payment-link-detail-page">
        <button className="back-btn" onClick={handleBack}>
          <ArrowLeft size={18} />
          Back to API keys
        </button>

        <div className="page-header">
          <div>
            <span className="detail-label">API KEY</span>
            <h1>{selectedApiKey.name}</h1>
            <p>{selectedApiKey.description || 'View payment sessions for this API key'}</p>
          </div>
        </div>

        <div className="info-cards-row">
          <div className="info-card">
            <div className="info-card-header">
              <Key size={16} />
              <span>API Key</span>
            </div>
            <div className="info-card-content">
              <code>{selectedApiKey.apiKey}</code>
              <button
                className="btn-icon small"
                onClick={() => {
                  navigator.clipboard.writeText(selectedApiKey.apiKey);
                  toast.success('API Key copied!');
                }}
              >
                <Copy size={14} />
              </button>
            </div>
          </div>
          {selectedApiKey.webhookUrl && (
            <div className="info-card">
              <div className="info-card-header">
                <ExternalLink size={16} />
                <span>Webhook URL</span>
              </div>
              <div className="info-card-content">
                <code>{selectedApiKey.webhookUrl}</code>
              </div>
            </div>
          )}
        </div>

        <div className="card">
          <h3 className="section-title" style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-color)' }}>
            Payment Sessions
          </h3>
          {isLoadingSessions ? (
            <TableSkeleton rows={4} cols={5} />
          ) : (
            <DataTable
              columns={sessionColumns}
              data={paymentSessions}
              searchable={true}
              filterable={true}
              sortable={true}
              paginated={true}
              pageSize={sessionsPagination.limit}
              emptyMessage="No payment sessions yet. Sessions are created when customers use this API key to make payments."
              serverSidePagination={true}
              totalRecords={sessionsPagination.total}
              currentServerPage={sessionsPagination.page}
              onPageChange={handleSessionsPageChange}
              onRowClick={handleSessionClick}
              onDateRangeChange={handleSessionsDateRangeChange}
              controlledDateRange={sessionsDateRange}
            />
          )}
        </div>

        <div className={`drawer-overlay ${showSessionDrawer ? 'open' : ''}`} onClick={handleCloseSessionDrawer} />
        <div className={`drawer drawer-large ${showSessionDrawer ? 'open' : ''}`}>
          <div className="drawer-header">
            <h2>Payment Session Details</h2>
            <button className="drawer-close-btn" onClick={handleCloseSessionDrawer}>
              <X size={20} />
            </button>
          </div>
          <div className="drawer-body">
            {isLoadingSessionDetail ? (
              <div className="loading-container">
                <div className="loading-spinner" />
                <p>Loading session details...</p>
              </div>
            ) : selectedSession ? (
              <>
                <div className={`session-status-banner ${selectedSession.status}`}>
                  <span className={`status-badge-large ${selectedSession.status}`}>
                    {selectedSession.status}
                  </span>
                  <div className="session-amount-large">
                    ${formatUSD(selectedSession.amount)} {selectedSession.currency}
                  </div>
                </div>

                <div className="drawer-section">
                  <h3 className="drawer-section-title">Session Information</h3>
                  <div className="detail-grid">
                    <div className="detail-row">
                      <span className="detail-key">Session ID</span>
                      <span className="detail-value mono">{selectedSession.sessionId}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-key">Order ID</span>
                      <span className="detail-value">{selectedSession.orderId || '-'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-key">Description</span>
                      <span className="detail-value">{selectedSession.description || '-'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-key">Amount</span>
                      <span className="detail-value">${formatUSD(selectedSession.amount)} {selectedSession.currency}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-key">Amount Paid</span>
                      <span className={`detail-value ${selectedSession.totalPaidUsd >= selectedSession.amount ? 'text-success' : selectedSession.totalPaidUsd > 0 ? 'text-warning' : ''}`}>
                        ${formatUSD(selectedSession.totalPaidUsd || 0)}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-key">Status</span>
                      <span className={`status-badge ${selectedSession.status}`}>
                        {selectedSession.status}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-key">Created</span>
                      <span className="detail-value">{formatDate(selectedSession.createdAt)}</span>
                    </div>
                    {selectedSession.paidAt && (
                      <div className="detail-row">
                        <span className="detail-key">Paid At</span>
                        <span className="detail-value">{formatDate(selectedSession.paidAt)}</span>
                      </div>
                    )}
                    {selectedSession.expiresAt && (
                      <div className="detail-row">
                        <span className="detail-key">Expires At</span>
                        <span className="detail-value">{formatDate(selectedSession.expiresAt)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {(selectedSession.customerName || selectedSession.customerEmail) && (
                  <div className="drawer-section">
                    <h3 className="drawer-section-title">Customer Information</h3>
                    <div className="detail-grid">
                      {selectedSession.customerName && (
                        <div className="detail-row">
                          <span className="detail-key">Name</span>
                          <span className="detail-value">{selectedSession.customerName}</span>
                        </div>
                      )}
                      {selectedSession.customerEmail && (
                        <div className="detail-row">
                          <span className="detail-key">Email</span>
                          <span className="detail-value">{selectedSession.customerEmail}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {(selectedSession.successUrl || selectedSession.cancelUrl || selectedSession.callbackUrl) && (
                  <div className="drawer-section">
                    <h3 className="drawer-section-title">Callback URLs</h3>
                    <div className="detail-grid">
                      {selectedSession.successUrl && (
                        <div className="detail-row">
                          <span className="detail-key">Success URL</span>
                          <span className="detail-value mono" style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>
                            {selectedSession.successUrl}
                          </span>
                        </div>
                      )}
                      {selectedSession.cancelUrl && (
                        <div className="detail-row">
                          <span className="detail-key">Cancel URL</span>
                          <span className="detail-value mono" style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>
                            {selectedSession.cancelUrl}
                          </span>
                        </div>
                      )}
                      {selectedSession.callbackUrl && (
                        <div className="detail-row">
                          <span className="detail-key">Webhook URL</span>
                          <span className="detail-value mono" style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>
                            {selectedSession.callbackUrl}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="drawer-section">
                  <h3 className="drawer-section-title">
                    Transactions ({sessionTransactions.length})
                  </h3>
                  {sessionTransactions.length > 0 ? (
                    <div className="session-transactions-list">
                      {sessionTransactions.map((tx, index) => (
                        <div key={tx.id || index} className="session-transaction-item">
                          <div className="tx-header">
                            <span className={`status-badge ${tx.status}`}>{tx.status}</span>
                            <span className="tx-amount">${formatUSD(tx.amountUsd || tx.amount)}</span>
                          </div>
                          <div className="tx-details">
                            {tx.txHash && (
                              <div className="tx-detail-row">
                                <span className="tx-label">Tx Hash:</span>
                                <span className="tx-value mono">
                                  {tx.txHash.substring(0, 10)}...{tx.txHash.substring(tx.txHash.length - 8)}
                                  <button
                                    className="btn-icon small"
                                    onClick={() => {
                                      navigator.clipboard.writeText(tx.txHash);
                                      toast.success('Transaction hash copied!');
                                    }}
                                  >
                                    <Copy size={12} />
                                  </button>
                                  {tx.explorerUrl && (
                                    <button
                                      className="btn-icon small"
                                      onClick={() => window.open(tx.explorerUrl, '_blank')}
                                    >
                                      <ExternalLink size={12} />
                                    </button>
                                  )}
                                </span>
                              </div>
                            )}
                            {tx.fromAddress && (
                              <div className="tx-detail-row">
                                <span className="tx-label">From:</span>
                                <span className="tx-value mono">
                                  {tx.fromAddress.substring(0, 8)}...{tx.fromAddress.substring(tx.fromAddress.length - 6)}
                                </span>
                              </div>
                            )}
                            {tx.tokenAmount && (
                              <div className="tx-detail-row">
                                <span className="tx-label">Amount:</span>
                                <span className="tx-value">{tx.tokenAmount} {tx.tokenSymbol || selectedSession.tokenSymbol}</span>
                              </div>
                            )}
                            <div className="tx-detail-row">
                              <span className="tx-label">Date:</span>
                              <span className="tx-value">{formatDate(tx.createdAt)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-transactions">
                      <Activity size={24} />
                      <p>No transactions recorded yet</p>
                    </div>
                  )}
                </div>

                {selectedSession.metadata && Object.keys(selectedSession.metadata).length > 0 && (
                  <div className="drawer-section">
                    <h3 className="drawer-section-title">Metadata</h3>
                    <div className="metadata-block">
                      <pre>{JSON.stringify(selectedSession.metadata, null, 2)}</pre>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="empty-state">
                <p>No session selected</p>
              </div>
            )}
          </div>
          <div className="drawer-footer">
            <button className="btn-secondary" onClick={handleCloseSessionDrawer}>
              Close
            </button>
            {selectedSession?.paymentUrl && (
              <button
                className="btn-primary"
                onClick={() => window.open(selectedSession.paymentUrl, '_blank')}
              >
                <ExternalLink size={16} />
                Open Payment Page
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (currentView === 'paymentLinks' && selectedApiKey) {
    return (
      <div className="payment-link-detail-page">
        <button className="back-btn" onClick={handleBack}>
          <ArrowLeft size={18} />
          Back to API keys
        </button>

        <div className="page-header">
          <div>
            <span className="detail-label">API KEY</span>
            <h1>{selectedApiKey.name}</h1>
            <p>{selectedApiKey.description || 'Manage payment links for this API key'}</p>
          </div>
          <button className="btn-primary" onClick={() => setShowCreateLinkModal(true)}>
            <Plus size={18} />
            Create Payment Link
          </button>
        </div>

        <div className="info-cards-row">
          <div className="info-card">
            <div className="info-card-header">
              <Key size={16} />
              <span>API Key</span>
            </div>
            <div className="info-card-content">
              <code>{selectedApiKey.apiKey}</code>
              <button
                className="btn-icon small"
                onClick={() => {
                  navigator.clipboard.writeText(selectedApiKey.apiKey);
                  toast.success('API Key copied!');
                }}
              >
                <Copy size={14} />
              </button>
            </div>
          </div>
          {selectedApiKey.webhookUrl && (
            <div className="info-card">
              <div className="info-card-header">
                <ExternalLink size={16} />
                <span>Webhook URL</span>
              </div>
              <div className="info-card-content">
                <code>{selectedApiKey.webhookUrl}</code>
              </div>
            </div>
          )}
        </div>

        <div className="card">
          <h3 className="section-title" style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-color)' }}>
            Payment Links
          </h3>
          {isLoadingLinks ? (
            <TableSkeleton rows={4} cols={7} />
          ) : (
            <DataTable
              columns={linkColumns}
              data={paymentLinks}
              searchable={true}
              sortable={true}
              paginated={true}
              pageSize={linkPagination.limit}
              emptyMessage="No payment links yet. Create your first payment link to get started."
              onRowClick={handleLinkClick}
              serverSidePagination={true}
              totalRecords={linkPagination.total}
              currentServerPage={linkPagination.page}
              onPageChange={handleLinkPageChange}
            />
          )}
        </div>

        <div className={`drawer-overlay ${showCreateLinkModal ? 'open' : ''}`} onClick={handleCloseLinkModal} />
        <div className={`drawer ${showCreateLinkModal ? 'open' : ''}`}>
          <div className="drawer-header">
            <h2>Create Payment Link</h2>
            <button className="drawer-close-btn" onClick={handleCloseLinkModal}>
              <X size={20} />
            </button>
          </div>
          <div className="drawer-body">
            <p className="drawer-info">
              Create a payment link associated with "{selectedApiKey.name}".
              This link will use the same API key for authentication.
            </p>

            <div className="form-group">
              <label>Link Name *</label>
              <input
                type="text"
                placeholder="e.g., Product Checkout, Subscription Payment"
                value={linkForm.name}
                onChange={(e) => setLinkForm({ ...linkForm, name: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Description (Optional)</label>
              <textarea
                placeholder="Describe where this link will be used..."
                rows={3}
                value={linkForm.description}
                onChange={(e) => setLinkForm({ ...linkForm, description: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Webhook URL (Optional)</label>
              <input
                type="url"
                placeholder="https://your-site.com/webhooks/payment"
                value={linkForm.webhookUrl}
                onChange={(e) => setLinkForm({ ...linkForm, webhookUrl: e.target.value })}
              />
              <span className="form-hint">Override the API key's default webhook URL</span>
            </div>

            <div className="info-box">
              <h4>Usage:</h4>
              <code style={{ display: 'block', fontSize: '12px', marginTop: '8px' }}>
                /pay/link/[linkId]?amount=100&currency=USD
              </code>
            </div>
          </div>
          <div className="drawer-footer">
            <button className="btn-secondary" onClick={handleCloseLinkModal}>
              Cancel
            </button>
            <button
              className="btn-primary"
              onClick={handleCreatePaymentLink}
              disabled={isCreating || !linkForm.name}
            >
              {isCreating ? 'Creating...' : 'Create Payment Link'}
            </button>
          </div>
        </div>
      </div>
    );
  }


 
  return (
    <div className="payment-links-page">
      <div className="page-header">
        <div>
          <h1>Developer API</h1>
          <p>{isAdmin ? 'View and manage all merchant API keys' : 'Manage your merchant API keys for payment integrations'}</p>
        </div>
        <div className="header-actions">
     
          {isAdmin && (
            <select
              className="merchant-filter"
              value={selectedMerchant}
              onChange={handleMerchantChange}
            >
              <option value="all">All Merchants</option>
              {merchants.map((merchant) => (
                <option key={merchant.id} value={merchant.id}>
                  {merchant.businessName || merchant.email}
                </option>
              ))}
            </select>
          )}

<button
  className="mode-btn-purple"
  onClick={() => window.open('https://docs.maxelpay.com/', '_blank')}
>
  View API Documentation
</button>
        </div>
      </div>

      <div className="stats-row">
        <div className="stat-item">
          <span className="stat-label">Total Keys</span>
          <span className="stat-value">{apiKeyStats.total}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Active Keys</span>
          <span className="stat-value">{apiKeyStats.active}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Inactive Keys</span>
          <span className="stat-value">{apiKeyStats.inactive}</span>
        </div>
      </div>

      <div className="card">
        {isLoading ? (
          <TableSkeleton rows={4} cols={6} />
        ) : (
          <DataTable
            columns={apiKeyColumns}
            data={apiKeys}
            searchable={true}
            filterable={true}
            sortable={true}
            paginated={true}
            pageSize={apiKeyPagination.limit}
            emptyMessage="No API keys yet. Create your first API key to get started."
            onRowClick={handleApiKeyClick}
            serverSidePagination={true}
            totalRecords={apiKeyPagination.total}
            currentServerPage={apiKeyPagination.page}
            onPageChange={handleApiKeyPageChange}
            onDateRangeChange={handleApiKeysDateRangeChange}
            controlledDateRange={apiKeysDateRange}
          />
        )}
      </div>


      <div className={`drawer-overlay ${showCreateApiKeyModal ? 'open' : ''}`} onClick={handleCloseApiKeyModal} />
      <div className={`drawer ${showCreateApiKeyModal ? 'open' : ''}`}>
        <div className="drawer-header">
          <h2>Create API Key</h2>
          <button className="drawer-close-btn" onClick={handleCloseApiKeyModal}>
            <X size={20} />
          </button>
        </div>
        <div className="drawer-body">
          <p className="drawer-info">
            Create a new API key for your payment integrations. Each API key can have multiple payment links.
          </p>

          <div className="form-group">
            <label>Key Name *</label>
            <input
              type="text"
              placeholder="e.g., E-Commerce Store, Mobile App"
              value={apiKeyForm.name}
              onChange={(e) => setApiKeyForm({ ...apiKeyForm, name: e.target.value })}
            />
            <span className="form-hint">A descriptive name to identify this API key</span>
          </div>

          <div className="form-group">
            <label>Description (Optional)</label>
            <textarea
              placeholder="Describe the platform or use case..."
              rows={3}
              value={apiKeyForm.description}
              onChange={(e) => setApiKeyForm({ ...apiKeyForm, description: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label>Default Webhook URL (Optional)</label>
            <input
              type="url"
              placeholder="https://your-site.com/webhooks/payment"
              value={apiKeyForm.webhookUrl}
              onChange={(e) => setApiKeyForm({ ...apiKeyForm, webhookUrl: e.target.value })}
            />
            <span className="form-hint">Default webhook for all payment links using this key</span>
          </div>

          <div className="info-box">
            <h4>How to use:</h4>
            <ol>
              <li>Create an API key to get started</li>
              <li>Add payment links under this API key</li>
              <li>Use the API key in your request headers:<br/>
                <code>X-API-KEY: pk_live_...</code><br/>
                <code>X-API-SECRET: sk_...</code> (server-side only)
              </li>
              <li>Redirect customers to the payment link with amount:<br/>
                <code>/pay/link/[linkId]?amount=100&currency=USD</code>
              </li>
            </ol>
          </div>
        </div>
        <div className="drawer-footer">
          <button className="btn-secondary" onClick={handleCloseApiKeyModal}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={handleCreateApiKey}
            disabled={isCreating || !apiKeyForm.name}
          >
            {isCreating ? 'Creating...' : 'Create API Key'}
          </button>
        </div>
      </div>

      {deleteModal.show && (
        <>
          <div className="modal-overlay" onClick={() => setDeleteModal({ show: false, type: '', id: null, name: '' })} />
          <div className="confirm-modal">
            <div className="confirm-modal-icon danger">
              <Trash2 size={24} />
            </div>
            <h3>{deleteModal.type === 'apiKey' ? 'Revoke API Key' : 'Delete Payment Link'}</h3>
            <p>
              Are you sure you want to {deleteModal.type === 'apiKey' ? 'revoke' : 'delete'} <strong>"{deleteModal.name}"</strong>?
              {deleteModal.type === 'apiKey' && ' This will invalidate all payment links using this API key.'}
            </p>
            <div className="confirm-modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setDeleteModal({ show: false, type: '', id: null, name: '' })}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="btn-danger"
                onClick={confirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Processing...' : (deleteModal.type === 'apiKey' ? 'Revoke' : 'Delete')}
              </button>
            </div>
          </div>
        </>
      )}

      <div className={`drawer-overlay ${showUsageGuideDrawer ? 'open' : ''}`} onClick={() => setShowUsageGuideDrawer(false)} />
      <div className={`drawer drawer-large ${showUsageGuideDrawer ? 'open' : ''}`}>
        <div className="drawer-header">
          <h2><BookOpen size={20} /> Integration Guide</h2>
          <button className="drawer-close-btn" onClick={() => setShowUsageGuideDrawer(false)}>
            <X size={20} />
          </button>
        </div>
        <div className="drawer-body usage-guide-content">
          <div className="guide-section">
            <div className="guide-section-header">
              <Zap size={20} />
              <h3>Quick Start</h3>
            </div>
            <div className="guide-steps">
              <div className="guide-step">
                <span className="step-number">1</span>
                <div className="step-content">
                  <h4>Create an API Key</h4>
                  <p>Click "Create API Key" to generate your unique API key. Keep this key secure - it's used to authenticate your payment requests.</p>
                </div>
              </div>
              <div className="guide-step">
                <span className="step-number">2</span>
                <div className="step-content">
                  <h4>Generate Payment URL</h4>
                  <p>Call our API from your backend to generate a secure, signed payment URL with the customer's cart amount.</p>
                </div>
              </div>
              <div className="guide-step">
                <span className="step-number">3</span>
                <div className="step-content">
                  <h4>Redirect Customer</h4>
                  <p>When customer clicks "Pay Now", redirect them to the signed payment URL. They'll complete payment on our secure page.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="guide-section">
            <div className="guide-section-header">
              <Terminal size={20} />
              <h3>API Endpoint</h3>
            </div>
            <div className="code-block">
              <div className="code-header">
                <span className="method post">POST</span>
                <span className="endpoint">/api/api-keys/generate-signed-url</span>
                <button
                  className="copy-btn"
                  onClick={() => {
                    navigator.clipboard.writeText(`${API_BASE_URL}/api-keys/generate-signed-url`);
                    toast.success('Endpoint copied!');
                  }}
                >
                  <Copy size={14} />
                </button>
              </div>
              <div className="code-description">
                <p>Generate a secure, tamper-proof payment URL with the specified amount.</p>
              </div>
            </div>

            <div className="params-section">
              <h4>Headers</h4>
              <div className="param-item">
                <code className="param-name">X-API-Key</code>
                <span className="param-required">required</span>
                <p className="param-desc">Your API key (e.g., pk_live_xxxxxxxx)</p>
              </div>
              <div className="param-item">
                <code className="param-name">X-API-Secret</code>
                <span className="param-required">required</span>
                <p className="param-desc">Your API secret (sk_...), shown when the key is created and under API Keys. Server-side only.</p>
              </div>
              <div className="param-item">
                <code className="param-name">Content-Type</code>
                <span className="param-required">required</span>
                <p className="param-desc">application/json</p>
              </div>
            </div>

            <div className="params-section">
              <h4>Request Body</h4>
              <div className="param-item">
                <code className="param-name">amount</code>
                <span className="param-required">required</span>
                <p className="param-desc">Payment amount (e.g., 150.00)</p>
              </div>
              <div className="param-item">
                <code className="param-name">currency</code>
                <span className="param-optional">optional</span>
                <p className="param-desc">Currency code (default: "USD")</p>
              </div>
              <div className="param-item">
                <code className="param-name">return_url</code>
                <span className="param-optional">optional</span>
                <p className="param-desc">URL to redirect customer after successful payment (e.g., "https://yoursite.com/order-complete")</p>
              </div>
            </div>

            <div className="params-section">
              <h4>Response</h4>
              <pre className="response-example">
{`{
  "success": true,
  "data": {
    "paymentUrl": "https://pay.example.com/pay/abc123?amount=150&currency=USD&sig=...&return_url=...",
    "linkId": "abc123def456",
    "amount": 150,
    "currency": "USD",
    "signature": "a1b2c3d4e5f6...",
    "return_url": "https://yoursite.com/order-complete"
  }
}`}
              </pre>
            </div>
          </div>

          <div className="guide-section">
            <div className="guide-section-header">
              <Code size={20} />
              <h3>Code Examples</h3>
            </div>

            <div className="code-example">
              <div className="code-example-header">
                <span className="lang-badge nodejs">Node.js / Express</span>
                <button
                  className="copy-btn"
                  onClick={() => {
                    navigator.clipboard.writeText(`const express = require('express');
const app = express();

// Your MaxelPay API key (keep this SECRET on your server!)
const MAXELPAY_API_KEY = 'pk_live_xxxxxxxxxxxxxxxxxxxxxxxx';
const MAXELPAY_API_SECRET = process.env.MAXELPAY_API_SECRET; // sk_..., never in the browser
const MAXELPAY_API_URL = '${API_BASE_URL}';

app.post('/checkout', async (req, res) => {
  const { cartTotal, orderId } = req.body; // e.g., 150.00

  try {
    // Call MaxelPay API to generate signed payment URL
    const response = await fetch(\`\${MAXELPAY_API_URL}/api-keys/generate-signed-url\`, {
      method: 'POST',
      headers: {
        'X-API-Key': MAXELPAY_API_KEY,
        'X-API-Secret': MAXELPAY_API_SECRET,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        amount: cartTotal,
        currency: 'USD',
        // Optional: URL to redirect after payment success
        return_url: \`https://yoursite.com/order-complete?id=\${orderId}\`,
      }),
    });

    const data = await response.json();

    if (data.success) {
      // Redirect customer to secure payment page
      res.redirect(data.data.paymentUrl);
    } else {
      res.status(400).json({ error: data.message });
    }
  } catch (error) {
    res.status(500).json({ error: 'Payment initialization failed' });
  }
});`);
                    toast.success('Node.js code copied!');
                  }}
                >
                  <Copy size={14} /> Copy
                </button>
              </div>
              <pre className="code-content">
{`const express = require('express');
const app = express();

// Your MaxelPay API key (keep this SECRET on your server!)
const MAXELPAY_API_KEY = 'pk_live_xxxxxxxxxxxxxxxxxxxxxxxx';
const MAXELPAY_API_SECRET = process.env.MAXELPAY_API_SECRET; // sk_..., never in the browser
const MAXELPAY_API_URL = '${API_BASE_URL}';

app.post('/checkout', async (req, res) => {
  const { cartTotal, orderId } = req.body; // e.g., 150.00

  try {
    // Call MaxelPay API to generate signed payment URL
    const response = await fetch(\`\${MAXELPAY_API_URL}/api-keys/generate-signed-url\`, {
      method: 'POST',
      headers: {
        'X-API-Key': MAXELPAY_API_KEY,
        'X-API-Secret': MAXELPAY_API_SECRET,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        amount: cartTotal,
        currency: 'USD',
        // Optional: URL to redirect after payment success
        return_url: \`https://yoursite.com/order-complete?id=\${orderId}\`,
      }),
    });

    const data = await response.json();

    if (data.success) {
      // Redirect customer to secure payment page
      res.redirect(data.data.paymentUrl);
    } else {
      res.status(400).json({ error: data.message });
    }
  } catch (error) {
    res.status(500).json({ error: 'Payment initialization failed' });
  }
});`}
              </pre>
            </div>

            <div className="code-example">
              <div className="code-example-header">
                <span className="lang-badge php">PHP</span>
                <button
                  className="copy-btn"
                  onClick={() => {
                    navigator.clipboard.writeText(`<?php
// Your MaxelPay API key (keep this SECRET on your server!)
$apiKey = 'pk_live_xxxxxxxxxxxxxxxxxxxxxxxx';
$apiSecret = getenv('MAXELPAY_API_SECRET'); // sk_..., never in the browser
$apiUrl = '${API_BASE_URL}/api-keys/generate-signed-url';

// Get cart total and order ID from your e-commerce system
$cartTotal = $_POST['cartTotal']; // e.g., 150.00
$orderId = $_POST['orderId'];

// Call MaxelPay API
$ch = curl_init($apiUrl);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    'X-API-Key: ' . $apiKey,
    'X-API-Secret: ' . $apiSecret,
    'Content-Type: application/json',
]);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode([
    'amount' => $cartTotal,
    'currency' => 'USD',
    // Optional: URL to redirect after payment success
    'return_url' => 'https://yoursite.com/order-complete?id=' . $orderId,
]));

$response = json_decode(curl_exec($ch), true);
curl_close($ch);

if ($response['success']) {
    // Redirect customer to secure payment page
    header('Location: ' . $response['data']['paymentUrl']);
    exit;
} else {
    die('Payment initialization failed: ' . $response['message']);
}
?>`);
                    toast.success('PHP code copied!');
                  }}
                >
                  <Copy size={14} /> Copy
                </button>
              </div>
              <pre className="code-content">
{`<?php
// Your MaxelPay API key (keep this SECRET on your server!)
$apiKey = 'pk_live_xxxxxxxxxxxxxxxxxxxxxxxx';
$apiSecret = getenv('MAXELPAY_API_SECRET'); // sk_..., never in the browser
$apiUrl = '${API_BASE_URL}/api-keys/generate-signed-url';

// Get cart total and order ID from your e-commerce system
$cartTotal = $_POST['cartTotal']; // e.g., 150.00
$orderId = $_POST['orderId'];

// Call MaxelPay API
$ch = curl_init($apiUrl);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    'X-API-Key: ' . $apiKey,
    'X-API-Secret: ' . $apiSecret,
    'Content-Type: application/json',
]);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode([
    'amount' => $cartTotal,
    'currency' => 'USD',
    // Optional: URL to redirect after payment success
    'return_url' => 'https://yoursite.com/order-complete?id=' . $orderId,
]));

$response = json_decode(curl_exec($ch), true);
curl_close($ch);

if ($response['success']) {
    // Redirect customer to secure payment page
    header('Location: ' . $response['data']['paymentUrl']);
    exit;
} else {
    die('Payment initialization failed: ' . $response['message']);
}
?>`}
              </pre>
            </div>

            <div className="code-example">
              <div className="code-example-header">
                <span className="lang-badge python">Python / Flask</span>
                <button
                  className="copy-btn"
                  onClick={() => {
                    navigator.clipboard.writeText(`import os
import requests
from flask import Flask, request, redirect

app = Flask(__name__)

# Your MaxelPay API key (keep this SECRET on your server!)
MAXELPAY_API_KEY = 'pk_live_xxxxxxxxxxxxxxxxxxxxxxxx'
MAXELPAY_API_SECRET = os.environ['MAXELPAY_API_SECRET']  # sk_..., never in the browser
MAXELPAY_API_URL = '${API_BASE_URL}'

@app.route('/checkout', methods=['POST'])
def checkout():
    cart_total = request.json.get('cartTotal')  # e.g., 150.00
    order_id = request.json.get('orderId')

    # Call MaxelPay API to generate signed payment URL
    response = requests.post(
        f'{MAXELPAY_API_URL}/api-keys/generate-signed-url',
        headers={
            'X-API-Key': MAXELPAY_API_KEY,
            'X-API-Secret': MAXELPAY_API_SECRET,
            'Content-Type': 'application/json',
        },
        json={
            'amount': cart_total,
            'currency': 'USD',
            # Optional: URL to redirect after payment success
            'return_url': f'https://yoursite.com/order-complete?id={order_id}',
        }
    )

    data = response.json()

    if data.get('success'):
        # Redirect customer to secure payment page
        return redirect(data['data']['paymentUrl'])
    else:
        return {'error': data.get('message')}, 400`);
                    toast.success('Python code copied!');
                  }}
                >
                  <Copy size={14} /> Copy
                </button>
              </div>
              <pre className="code-content">
{`import os
import requests
from flask import Flask, request, redirect

app = Flask(__name__)

# Your MaxelPay API key (keep this SECRET on your server!)
MAXELPAY_API_KEY = 'pk_live_xxxxxxxxxxxxxxxxxxxxxxxx'
MAXELPAY_API_SECRET = os.environ['MAXELPAY_API_SECRET']  # sk_..., never in the browser
MAXELPAY_API_URL = '${API_BASE_URL}'

@app.route('/checkout', methods=['POST'])
def checkout():
    cart_total = request.json.get('cartTotal')  # e.g., 150.00
    order_id = request.json.get('orderId')

    # Call MaxelPay API to generate signed payment URL
    response = requests.post(
        f'{MAXELPAY_API_URL}/api-keys/generate-signed-url',
        headers={
            'X-API-Key': MAXELPAY_API_KEY,
            'X-API-Secret': MAXELPAY_API_SECRET,
            'Content-Type': 'application/json',
        },
        json={
            'amount': cart_total,
            'currency': 'USD',
            # Optional: URL to redirect after payment success
            'return_url': f'https://yoursite.com/order-complete?id={order_id}',
        }
    )

    data = response.json()

    if data.get('success'):
        # Redirect customer to secure payment page
        return redirect(data['data']['paymentUrl'])
    else:
        return {'error': data.get('message')}, 400`}
              </pre>
            </div>
          </div>

          <div className="guide-section">
            <div className="guide-section-header">
              <Shield size={20} />
              <h3>Security</h3>
            </div>
            <div className="security-info">
              <div className="security-item">
                <h4>Why use signed URLs?</h4>
                <p>The payment URL includes a cryptographic signature (HMAC-SHA256) that prevents customers from tampering with the amount. If anyone modifies the amount in the URL, the signature won't match and the payment will be rejected.</p>
              </div>
              <div className="security-item">
                <h4>Keep your API key secret</h4>
                <p>Never expose your API key in frontend code or public repositories. Only use it on your backend server where it cannot be accessed by customers.</p>
              </div>
              <div className="security-item warning">
                <h4>⚠️ What happens if someone tampers with the URL?</h4>
                <table className="security-table">
                  <thead>
                    <tr>
                      <th>Scenario</th>
                      <th>Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Valid URL with correct signature</td>
                      <td><span className="result success">✓ Payment proceeds</span></td>
                    </tr>
                    <tr>
                      <td>Amount changed in URL</td>
                      <td><span className="result error">✗ Rejected - Invalid signature</span></td>
                    </tr>
                    <tr>
                      <td>Signature removed from URL</td>
                      <td><span className="result error">✗ Rejected - Signature required</span></td>
                    </tr>
                    <tr>
                      <td>Invalid signature</td>
                      <td><span className="result error">✗ Rejected - Tamper detected</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="guide-section">
            <div className="guide-section-header">
              <Activity size={20} />
              <h3>Payment Flow</h3>
            </div>
            <div className="flow-diagram">
              <div className="flow-step">
                <div className="flow-icon customer">👤</div>
                <div className="flow-label">Customer</div>
                <div className="flow-desc">Clicks "Pay Now" on your site</div>
              </div>
              <div className="flow-arrow">→</div>
              <div className="flow-step">
                <div className="flow-icon server">🖥️</div>
                <div className="flow-label">Your Server</div>
                <div className="flow-desc">Calls MaxelPay API with amount</div>
              </div>
              <div className="flow-arrow">→</div>
              <div className="flow-step">
                <div className="flow-icon api">🔐</div>
                <div className="flow-label">MaxelPay API</div>
                <div className="flow-desc">Returns signed payment URL</div>
              </div>
              <div className="flow-arrow">→</div>
              <div className="flow-step">
                <div className="flow-icon redirect">↗️</div>
                <div className="flow-label">Redirect</div>
                <div className="flow-desc">Customer sent to payment page</div>
              </div>
              <div className="flow-arrow">→</div>
              <div className="flow-step">
                <div className="flow-icon payment">💳</div>
                <div className="flow-label">Payment</div>
                <div className="flow-desc">Customer pays with crypto</div>
              </div>
            </div>
          </div>

        </div>
        <div className="drawer-footer">
          <button className="btn-secondary" onClick={() => setShowUsageGuideDrawer(false)}>
            Close
          </button>
          <button
            className="btn-primary"
            onClick={() => {
              setShowUsageGuideDrawer(false);
              setShowCreateApiKeyModal(true);
            }}
          >
            <Plus size={16} />
            Create API Key
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentLinks;
